/**
 * SupportSlaStrip — the SLA-breach INDICATORS atop the Support screen: a cross-lab preview of every open
 * ticket whose age exceeds its per-priorite SLA target, with the overdue magnitude, the due timestamp, and
 * whether a breach alert has been queued. The data source is the sweep's DRY-RUN preview (queues no mail);
 * a "Notify labs" action fires the REAL sweep (queues the idempotent breach mail per ticket) on demand.
 *
 * DERIVED, additive: it does not touch the inbox read — it drives its own `useSlaSweepPreview` query that
 * polls on a slow cadence and re-derives whenever a workflow write invalidates the management root. When
 * there are no breaches the strip collapses to a single reassuring line; on empty/loading it renders nothing
 * (the parent screen already cleared offline/forbidden). Full 5-state DoD relative to its own data source.
 *
 * Accessibility: the Card is a <section> landmark headed "Dépassements de SLA"; the breached table carries a
 * caption; the overdue badge pairs tone with the hours text (never color-only); each breached row's "open"
 * action names the ticket reference. AA contrast via tokens.
 */
import { useMemo } from "react";
import { Badge, Button, Card, ErrorState, Table, useToast, type Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { formatDateTime } from "../lib";
import { prioriteGlyph, prioriteLabelKey, prioriteTone } from "../support.lib";
import { isManagementAbsent } from "../api";
import { slaOverdueTone } from "../supportWorkflow.lib";
import { useRunSlaSweep, useSlaSweepPreview } from "../supportWorkflow.api";
import type { SlaBreachedTicket } from "../supportWorkflow.types";
import { StatChips } from "./Distribution";

export interface SupportSlaStripProps {
  /** Gate (super-admin && online) — also gates the preview poll so a forbidden/offline strip never polls. */
  enabled: boolean;
  /** Select a breached ticket (drives the chat/triage panes on the parent screen). */
  onSelect?: (id: string) => void;
}

/** Cap the breached rows rendered inline; the count chip carries the true total. */
const MAX_ROWS = 8;

export function SupportSlaStrip({ enabled, onSelect }: SupportSlaStripProps) {
  const { t } = useT();
  const locale = useUiStore((s) => s.locale);
  const toast = useToast();

  const preview = useSlaSweepPreview(enabled);
  const runSweep = useRunSlaSweep();

  const report = preview.data;
  const breached = useMemo(() => report?.breachedTickets ?? [], [report]);
  const rows = useMemo(() => breached.slice(0, MAX_ROWS), [breached]);

  function onNotify(): void {
    runSweep.mutate(
      {},
      {
        onSuccess: (res) =>
          res.emailsQueued > 0
            ? toast.success(t("mgmt.support.wf.sla.notified", { n: res.emailsQueued }))
            : toast.success(t("mgmt.support.wf.sla.notified.none")),
        onError: () => toast.error(t("mgmt.support.wf.sla.notify.error")),
      },
    );
  }

  // Loading of the very first preview → render nothing (the parent has already cleared the screen states).
  if (preview.isLoading) return null;

  // A cloud-only route 404 on a local box is the parent's "unavailable" case — swallow it here (render nothing).
  if (preview.isError && isManagementAbsent(preview.error)) return null;

  if (preview.isError) {
    return (
      <Card title={t("mgmt.support.wf.sla.title")}>
        <ErrorState
          message={preview.error.message}
          code={preview.error.code}
          onRetry={() => void preview.refetch()}
        />
      </Card>
    );
  }

  // No breaches → a single reassuring line (do not show a zeroed table).
  if (breached.length === 0) {
    return (
      <Card title={t("mgmt.support.wf.sla.title")}>
        <p role="status" style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
          {t("mgmt.support.wf.sla.none")}
        </p>
      </Card>
    );
  }

  const columns: ReadonlyArray<Column<SlaBreachedTicket>> = [
    {
      key: "reference",
      header: t("mgmt.support.wf.sla.col.reference"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.reference}</span>,
    },
    { key: "sujet", header: t("mgmt.support.wf.sla.col.sujet"), cell: (r) => r.sujet },
    {
      key: "priorite",
      header: t("mgmt.support.wf.sla.col.priorite"),
      cell: (r) => (
        <Badge tone={prioriteTone(r.sla.priorite)} glyph={prioriteGlyph(r.sla.priorite)}>
          {t(prioriteLabelKey(r.sla.priorite))}
        </Badge>
      ),
    },
    {
      key: "overdue",
      header: t("mgmt.support.wf.sla.col.overdue"),
      cell: (r) => (
        <Badge
          tone={slaOverdueTone(r.sla)}
          glyph="◔"
          aria-label={t("mgmt.support.wf.sla.overdue.aria", { ref: r.reference, h: r.sla.overdueHours })}
        >
          <span className="nv-ltr-island">{t("mgmt.support.wf.sla.overdue.hours", { h: r.sla.overdueHours })}</span>
        </Badge>
      ),
    },
    {
      key: "due",
      header: t("mgmt.support.wf.sla.col.due"),
      cell: (r) => <span className="nv-ltr-island">{formatDateTime(r.sla.dueAt, locale)}</span>,
    },
    {
      key: "emailed",
      header: t("mgmt.support.wf.sla.col.emailed"),
      align: "end",
      cell: (r) =>
        r.emailQueued ? (
          <Badge tone="info" glyph="✉">
            {t("mgmt.support.wf.sla.emailed.yes")}
          </Badge>
        ) : (
          <span aria-hidden style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("mgmt.support.wf.sla.emailed.no")}
          </span>
        ),
    },
    ...(onSelect
      ? [
          {
            key: "actions",
            header: "",
            align: "end" as const,
            cell: (r: SlaBreachedTicket) => (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`${t("mgmt.support.wf.sla.open")} ${r.reference}`}
                onClick={() => onSelect(r.id)}
              >
                {t("mgmt.support.wf.sla.open")}
              </Button>
            ),
          } as Column<SlaBreachedTicket>,
        ]
      : []),
  ];

  return (
    <Card
      title={t("mgmt.support.wf.sla.title")}
      actions={
        <Button
          variant="secondary"
          size="sm"
          onClick={onNotify}
          loading={runSweep.isPending}
        >
          {runSweep.isPending ? t("mgmt.support.wf.sla.notifying") : t("mgmt.support.wf.sla.notify")}
        </Button>
      }
    >
      {/* The Card is a <section> with an <h2> heading — it is the landmark; no redundant region wrapper. */}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        <StatChips
          chips={[
            { key: "scanned", label: t("mgmt.support.wf.sla.scanned"), value: report?.scanned ?? 0 },
            {
              key: "breached",
              label: t("mgmt.support.wf.sla.breached"),
              value: report?.breached ?? 0,
              tone: (report?.breached ?? 0) > 0 ? "danger" : "success",
            },
            { key: "emailed", label: t("mgmt.support.wf.sla.emailed"), value: report?.emailsQueued ?? 0 },
          ]}
        />

        <Table
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          caption={t("mgmt.support.wf.sla.title")}
          {...(onSelect ? { onRowClick: (r: SlaBreachedTicket) => onSelect(r.id) } : {})}
        />

        {breached.length > rows.length && (
          <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("mgmt.support.wf.sla.more", { n: breached.length - rows.length })}
          </p>
        )}
      </div>
    </Card>
  );
}
